import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PageHeader from "./ui/PageHeader";
import { ArrowUpRight } from "./ui/Icons";
import heroImage from "../image/potatoes-in-garden-bed.jpg";

const HERO_ALT =
  "A cluster of potatoes freshly lifted from dark soil, roots and foliage still attached";

const STATS = [
  { num: "200K", label: "deaths a year linked to pesticide exposure" },
  { num: "40–50%", label: "of crops lost to disease annually" },
  { num: "$850M", label: "GDP impact if that waste were eliminated" },
  { num: "93.8%", label: "disease detection accuracy" },
];

const IMPACTS = [
  {
    title: "Soil degradation",
    body: "Overused pesticides disrupt beneficial soil microorganisms, reducing fertility and ecosystem resilience.",
  },
  {
    title: "Climate change",
    body: "Chemical runoff and soil degradation contribute to greenhouse gas emissions and diminish natural carbon sequestration.",
  },
  {
    title: "Public health",
    body: "Continuous exposure to pesticide residues in food has been associated with chronic neurological disorders and cancer.",
  },
];

const OUTCOMES = [
  {
    title: "Enhanced soil health",
    body: "Minimising chemical overuse preserves beneficial microorganisms and supports soil fertility.",
  },
  {
    title: "Improved food security",
    body: "Healthier crops mean better yields and safer food, directly benefiting communities.",
  },
  {
    title: "Climate resilience",
    body: "Sustainable pesticide practices lower the carbon footprint of agricultural operations.",
  },
  {
    title: "Public health protection",
    body: "Reducing pesticide residues in food translates to fewer health risks for communities.",
  },
];

/* Ruled register — the page's one repeating device, used for both the harms
   and the outcomes so the two lists read as a matched pair. */
const Register = ({ items, columns = 1 }) => (
  <dl
    className={`my-10 grid grid-cols-1 gap-x-10 ${
      columns === 2 ? "sm:grid-cols-2" : ""
    }`}
  >
    {items.map((item, i) => (
      <div
        key={item.title}
        className="grid grid-cols-[2.5rem_1fr] gap-x-3 border-t border-agri-rule py-5"
      >
        <span className="pt-1 font-mono text-[0.6875rem] font-medium tabular-nums text-agri-clay">
          {String(i + 1).padStart(2, "0")}
        </span>
        <div>
          <dt className="font-display text-[1.0625rem] font-semibold leading-snug tracking-[-0.015em] text-agri-ink">
            {item.title}
          </dt>
          <dd className="mt-1.5 text-[0.9375rem] leading-[1.7] text-agri-ink80">
            {item.body}
          </dd>
        </div>
      </div>
    ))}
  </dl>
);

const HeroPlate = () => (
  <figure className="bg-agri-forestDeep">
    <img
      src={heroImage}
      alt={HERO_ALT}
      className="h-[16rem] w-full object-cover sm:h-[22rem] lg:h-[30rem]"
    />
  </figure>
);

const ClimateAction = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const shouldUseFrenchContent =
    i18n.language === "kir" || i18n.language === "fr";

  // French / Kirundi edition — same shell, plain-text body
  if (
    shouldUseFrenchContent &&
    t("climate_action_fr", { defaultValue: "" }) !== ""
  ) {
    const paragraphs = t("climate_action_fr").split("\n\n");
    return (
      <main className="bg-agri-paper">
        <PageHeader
          kicker="Climate action"
          title="AgriHyphen AI & Climate Action"
        />
        <HeroPlate />
        <div className="container-x section-tight">
          <div className="article">
            {paragraphs.map((paragraph, index) => {
              const isHeading =
                !paragraph.includes(".") && paragraph.length < 100;
              return isHeading ? (
                <h2 key={index}>{paragraph}</h2>
              ) : (
                <p key={index}>{paragraph}</p>
              );
            })}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-agri-paper">
      <PageHeader
        kicker="Climate action"
        title={t(
          "Agrihyphen AI: Empowering Sustainable Agriculture for a Healthier Planet"
        )}
      />
      <HeroPlate />

      <div className="container-x section-tight">
        <div className="grid grid-cols-1 gap-x-14 gap-y-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {/* Standfirst set larger than the body — the reader's entry point */}
            <p className="max-w-measureWide font-display text-[1.375rem] font-normal leading-[1.5] tracking-[-0.015em] text-agri-ink sm:text-[1.5rem]">
              Agriculture is the backbone of food security, yet many small-scale
              farmers face significant challenges. Limited access to modern
              technology and expert agronomists often forces them into decisions
              that can inadvertently harm both human health and the environment.
            </p>

            <div className="article mt-10">
              <p>
                In many regions, farmers lacking proper guidance resort to the
                unregulated use of pesticides — even ones unrelated to the
                specific plant diseases affecting their crops.
              </p>

              <h2>{t("The Hidden Toll of Pesticide Overuse")}</h2>
              <p>
                Improper and excessive pesticide application has far-reaching
                consequences. Studies suggest that adverse effects of pesticide
                exposure are linked to an estimated 200,000 premature deaths
                worldwide each year. These figures underscore the urgency of
                addressing pesticide misuse.
              </p>
            </div>

            <Register items={IMPACTS} />

            <div className="article">
              <h2>{t("Our Approach: Smart, Sustainable, and Data-Driven")}</h2>
              <p>
                At AgriHyphen AI we are committed to transforming agriculture
                into a more sustainable and climate-resilient industry. Our
                platform harnesses image recognition and machine learning to
                tackle these challenges head-on.
              </p>
            </div>

            <Register
              items={[
                {
                  title: t("Early Disease Detection"),
                  body: "Advanced image recognition analyses high-resolution photographs of crops, detecting plant diseases at their earliest stages.",
                },
                {
                  title: t("Tailored Recommendations for Pesticide Use"),
                  body: "Once a disease is diagnosed, AgriHyphen AI returns optimal dosage, targeted application areas, and eco-friendly biopesticide alternatives.",
                },
              ]}
            />

            <div className="article">
              <h2>
                {t("Restoring Ecosystem Balance and Advancing Climate Action")}
              </h2>
            </div>

            <Register items={OUTCOMES} columns={2} />

            <div className="article">
              <h2>{t("A Vision for the Future")}</h2>
              <p>
                AgriHyphen AI isn&rsquo;t only about disease detection and
                pesticide management — it&rsquo;s about reshaping the future of
                agriculture. By equipping farmers with practical technology and
                actionable insight, we aim to foster an agricultural landscape
                that is as kind to the planet as it is productive.
              </p>
              <blockquote>
                Join us in our mission to build a more sustainable and resilient
                world — one field at a time.
              </blockquote>
            </div>
          </div>

          {/* Sidebar: figures the article refers to, kept in view while reading */}
          <aside className="lg:col-span-4">
            <div className="space-y-10 lg:sticky lg:top-28">
              <section>
                <h2 className="kicker-rule">By the numbers</h2>
                <dl className="mt-5">
                  {STATS.map((s) => (
                    <div
                      key={s.num}
                      className="border-t border-agri-rule py-4 last:border-b last:border-agri-rule"
                    >
                      <dd className="figure text-[1.75rem] leading-none text-agri-ink">
                        {s.num}
                      </dd>
                      <dt className="mt-2 text-[0.8125rem] leading-snug text-agri-ink60">
                        {s.label}
                      </dt>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="bg-agri-forest p-7 text-white">
                <p className="kicker-light">Take action</p>
                <p className="mt-4 font-display text-[1.25rem] font-semibold leading-snug tracking-[-0.018em]">
                  Detect disease before you spray.
                </p>
                <p className="mt-3 text-[0.9375rem] leading-[1.65] text-white/65">
                  Run a photo through the model and get a targeted treatment
                  recommendation instead of a blanket application.
                </p>
                <Link to="/test-ai" className="btn-lime mt-6 w-full">
                  Try AgriHyphen AI
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </section>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default ClimateAction;
